/* Product page components: the mobile media carousel and the size-guide
   accordion's diagram/tab switcher. */

if (!customElements.get('nm-product-gallery')) {
  customElements.define(
    'nm-product-gallery',
    class NmProductGallery extends HTMLElement {
      connectedCallback() {
        this.segments = Array.from(this.querySelectorAll('.nm-product-gallery__progress-segment'));

        /* Full-screen viewer (nm-product-zoom), rendered by the section
           when the merchant has image zoom switched on. Splide swallows
           clicks that end a drag, so a swipe never opens it by accident. */
        this.querySelectorAll('[data-zoom-open]').forEach((button) => {
          button.addEventListener('click', () => {
            const viewer = this.closest('product-info')?.querySelector('nm-product-zoom');
            if (viewer && typeof viewer.open === 'function') {
              viewer.open(Number(button.dataset.zoomOpen), button);
            }
          });
        });

        if (this.querySelectorAll('.splide__slide').length < 2) return;
        if (typeof Splide === 'undefined') return;

        /* Carousel on mobile only. Splide's own responsive `destroy` is
           unreliable here, so drive mount/destroy from a media query: below
           750px it's a swipe carousel, at/above it's a plain CSS grid. */
        this.mql = window.matchMedia('(max-width: 749px)');
        this.syncListener = () => this.sync();
        this.mql.addEventListener('change', this.syncListener);
        this.sync();
      }

      disconnectedCallback() {
        this.mql?.removeEventListener('change', this.syncListener);
        this.teardown();
      }

      sync() {
        if (this.mql.matches) {
          this.mount();
        } else {
          this.teardown();
        }
      }

      mount() {
        if (this.splide) return;

        this.splide = new Splide(this, {
          type: 'slide',
          arrows: false,
          pagination: false,
          gap: 0,
          drag: true,
        });

        /* `move` passes the destination index — use it directly so the bar
           tracks the swipe as it happens rather than lagging a slide. */
        this.splide.on('mounted', () => this.updateProgress(this.splide.index));
        this.splide.on('move', (newIndex) => this.updateProgress(newIndex));
        this.splide.mount();
      }

      teardown() {
        if (!this.splide) return;
        this.splide.destroy(true);
        this.splide = null;

        /* Splide's destroy can leave state classes and inline styles
           behind; scrub them so the desktop grid CSS takes over cleanly. */
        this.classList.remove('is-initialized', 'is-active', 'is-overflow', 'is-rendered');
        this.querySelector('.splide__track')?.removeAttribute('style');
        const list = this.querySelector('.splide__list');
        if (list) list.removeAttribute('style');
        this.querySelectorAll('.splide__slide').forEach((slide) => {
          slide.removeAttribute('style');
          slide.classList.remove('is-active', 'is-visible', 'is-prev', 'is-next');
        });
      }

      updateProgress(index) {
        const active = typeof index === 'number' ? index : this.splide ? this.splide.index : 0;
        /* Fill cumulatively: every segment up to and including the current
           slide, so the indicator grows left-to-right as slides progress. */
        this.segments.forEach((segment, i) => {
          segment.classList.toggle('is-active', i <= active);
        });
      }
    }
  );
}

if (!customElements.get('nm-size-guide')) {
  customElements.define(
    'nm-size-guide',
    class NmSizeGuide extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[data-size-tab]'));
        this.diagrams = Array.from(this.querySelectorAll('[data-size-image]'));

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.activate(tab.dataset.sizeTab));
        });

        /* Follow the main size picker: when the selected variant changes,
           show that size's diagram. */
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data: { variant } }) => {
            const position = parseInt(this.dataset.optionPosition, 10);
            const value = variant && variant.options ? variant.options[position - 1] : undefined;
            if (value !== undefined && value !== null) this.activate(String(value));
          });
        }
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      activate(sizeValue) {
        if (!this.tabs.some((tab) => tab.dataset.sizeTab === sizeValue)) return;

        this.tabs.forEach((tab) => {
          tab.setAttribute('aria-pressed', tab.dataset.sizeTab === sizeValue ? 'true' : 'false');
        });
        this.diagrams.forEach((diagram) => {
          diagram.hidden = diagram.dataset.sizeImage !== sizeValue;
        });
      }
    }
  );
}
